/**
 * Ad Dashboard — the paid-acquisition dashboard, hosted in the admin shell.
 *
 * The dashboard is served whole by the admin backend at /api/ads/embed (its
 * data inlined, headline and KPI cards server-rendered) and framed here so its
 * own tokens and fonts stay isolated from the admin theme. The frame reports
 * its range and channel back by postMessage, which this page mirrors into the
 * address bar so /ads#30d-meta links to a particular view.
 *
 * Below it, "Published snapshots" lists the Tuesday and Friday publishes;
 * opening one swaps the frame to that stored HTML with a banner back to live.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Drawer, Skeleton, Stack, Typography } from '@mui/material';
import { History as HistoryIcon } from '@mui/icons-material';
import { APP_BAR_HEIGHT, rightDrawerBelowAppBarSx } from '../../theme/layoutConstants';
import SnapshotsPanel from './SnapshotsPanel';
import {
  AdsSnapshot, AdsSnapshotRecord, embedUrl, fetchSnapshot, fetchSnapshots, formatSnapshotDate,
} from './api';
import { AdsView, buildHash, isSameView, parseHash } from './hashState';

type Mode = { kind: 'live' } | { kind: 'snapshot'; snapshot: AdsSnapshot };

const AdDashboardPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const frameRef = useRef<HTMLIFrameElement | null>(null);

  // The frame rewrites the parent hash as the viewer filters, so its src is
  // fixed at mount: re-deriving it from the hash would reload on every click.
  const [liveSrc] = useState<string>(() => embedUrl(parseHash(location.hash)));
  const viewRef = useRef<AdsView>(parseHash(location.hash));

  const [snapshots, setSnapshots] = useState<AdsSnapshot[] | null>(null);
  const [snapshotsError, setSnapshotsError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'live' });
  const [record, setRecord] = useState<AdsSnapshotRecord | null>(null);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const requestedSnapshotDate = searchParams.get('snapshot');

  useEffect(() => {
    let cancelled = false;
    fetchSnapshots()
      .then((list) => { if (!cancelled) setSnapshots(list); })
      .catch((e: Error) => { if (!cancelled) setSnapshotsError(e.message); });
    return () => { cancelled = true; };
  }, []);

  // ?snapshot=YYYY-MM-DD is the snapshot_url the ads publish returns. Apply each
  // distinct value once: depending on `mode` here would re-open the snapshot the
  // moment "Back to live" flips the mode, before the param is removed.
  const appliedSnapshotParam = useRef<string | null>(null);
  useEffect(() => {
    if (!requestedSnapshotDate) { appliedSnapshotParam.current = null; return; }
    if (!snapshots || appliedSnapshotParam.current === requestedSnapshotDate) return;
    const match = snapshots.find((s) => s.date === requestedSnapshotDate);
    appliedSnapshotParam.current = requestedSnapshotDate;
    if (match) {
      setFrameLoaded(false);
      setMode({ kind: 'snapshot', snapshot: match });
    }
  }, [snapshots, requestedSnapshotDate]);

  useEffect(() => {
    if (mode.kind !== 'snapshot') { setRecord(null); setRecordError(null); return undefined; }
    let cancelled = false;
    setRecord(null);
    fetchSnapshot(mode.snapshot.id)
      .then((r) => { if (!cancelled) setRecord(r); })
      .catch((e: Error) => { if (!cancelled) setRecordError(e.message); });
    return () => { cancelled = true; };
  }, [mode]);

  // The dashboard tells the shell which range and channel it is showing.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      const data = event.data as { type?: string; hash?: string } | null;
      if (!data || data.type !== 'ls-ads:hash' || typeof data.hash !== 'string') return;
      const next = parseHash(data.hash);
      if (isSameView(next, viewRef.current) && location.hash === `#${data.hash}`) return;
      viewRef.current = next;
      navigate({ hash: `#${buildHash(next)}`, search: location.search }, { replace: true });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [navigate, location.hash, location.search]);

  const backToLive = useCallback(() => {
    setFrameLoaded(false);
    setMode({ kind: 'live' });
    if (searchParams.has('snapshot')) {
      const next = new URLSearchParams(searchParams);
      next.delete('snapshot');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const selectSnapshot = useCallback((snapshot: AdsSnapshot) => {
    appliedSnapshotParam.current = snapshot.date;
    setDrawerOpen(false);
    setFrameLoaded(false);
    setMode({ kind: 'snapshot', snapshot });
    const next = new URLSearchParams(searchParams);
    next.set('snapshot', snapshot.date);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const frame = useMemo(() => {
    if (mode.kind === 'live') {
      return (
        <iframe
          ref={frameRef}
          key="live"
          title="Ad Dashboard"
          src={liveSrc}
          onLoad={() => setFrameLoaded(true)}
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          style={{ border: 0, width: '100%', height: '100%', display: 'block', background: '#fff' }}
        />
      );
    }
    if (recordError) return <Alert severity="error" sx={{ m: 2 }}>Could not open that snapshot: {recordError}</Alert>;
    if (!record) return null;
    return (
      <iframe
        key={record.id}
        title={`Ad Dashboard snapshot ${record.date}`}
        srcDoc={record.html}
        onLoad={() => setFrameLoaded(true)}
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
        style={{ border: 0, width: '100%', height: '100%', display: 'block', background: '#fff' }}
      />
    );
  }, [mode, liveSrc, record, recordError]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: `calc(100vh - ${APP_BAR_HEIGHT}px)` }}>
      {mode.kind === 'snapshot' ? (
        <Alert
          severity="info"
          square
          sx={{ flexShrink: 0, borderRadius: 0, py: 0.25 }}
          action={(
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" startIcon={<HistoryIcon />} onClick={() => setDrawerOpen(true)}>Snapshots</Button>
              <Button color="inherit" size="small" variant="outlined" onClick={backToLive}>Back to live</Button>
            </Stack>
          )}
        >
          Frozen snapshot from {formatSnapshotDate(mode.snapshot.date)}. This is how the dashboard looked at that publish, not live data.
        </Alert>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ flexShrink: 0, px: 2, py: 0.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
        >
          <Typography variant="caption" color="text.secondary">
            Live data. Use the time range, dates and channel filters below.
          </Typography>
          <Button size="small" startIcon={<HistoryIcon />} onClick={() => setDrawerOpen(true)}>
            Snapshots{snapshots && snapshots.length ? ` (${snapshots.length})` : ''}
          </Button>
        </Stack>
      )}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {!frameLoaded && !recordError && (
          <Skeleton variant="rectangular" sx={{ position: 'absolute', inset: 0, zIndex: 1 }} />
        )}
        {frame}
      </Box>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: rightDrawerBelowAppBarSx }}
      >
        <SnapshotsPanel
          snapshots={snapshots}
          error={snapshotsError}
          selectedId={mode.kind === 'snapshot' ? mode.snapshot.id : null}
          onSelect={selectSnapshot}
        />
      </Drawer>
    </Box>
  );
};

export default AdDashboardPage;
